import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

import ArticleList from "../components/ArticleList";
import CategoryList from "../components/CategoryList";
import TagList from "../components/TagList";
import AuthorBox from "../components/AuthorBox";
const API_BASE = process.env.REACT_APP_API_BASE || "http://192.168.1.89:8000/api";

const AuthorPage = () => {
  const { username } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const [authorRes, articlesRes, catsRes, tagsRes] = await Promise.all([
          axios.get(`${API_BASE}/authors/${username}/`),
          axios.get(`${API_BASE}/authors/${username}/articles/`),
          axios.get(`${API_BASE}/categories/`),
          axios.get(`${API_BASE}/tags/`),
        ]);

        if (cancelled) return;

        setAuthor(authorRes.data);
        setArticles(articlesRes.data.results || articlesRes.data || []);
        setCategories(catsRes.data.results || catsRes.data || []);
        setTags(tagsRes.data.results || tagsRes.data || []);
      } catch (err) {
        if (cancelled) return;
        setAuthor(null);
        setArticles([]);
        setCategories([]);
        setTags([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [username]);

  const pageTitle = useMemo(() => {
    const name = author?.user?.username || author?.username || username || "Author";
    return `${name} | 24News`;
  }, [author, username]);

  const pageDescription = useMemo(() => {
    const name = author?.user?.username || author?.username || username || "author";
    const count = articles.length;
    return `Articles by ${name}${count ? ` (${count})` : ""} on 24News.`;
  }, [author, articles.length, username]);

  const authorImage = author?.image || author?.avatar || "";

  if (loading) return <div className="container"><p>Loading...</p></div>;
  if (!author) return <div className="container"><h2>Author not found</h2></div>;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:type" content="profile" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {authorImage ? <meta property="og:image" content={authorImage} /> : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {authorImage ? <meta name="twitter:image" content={authorImage} /> : null}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            mainEntity: {
              "@type": "Person",
              name: author?.user?.username || author?.username || username || "",
              image: authorImage || undefined,
            },
            description: pageDescription,
          })}
        </script>
          <link rel="canonical" href={`${window.location.origin}/authors/${username}`} />
      </Helmet>

      <div className="container" style={{ maxWidth: 1080, margin: "auto", padding: "32px 16px" }}>
        <AuthorBox author={author} />
        <h2 style={{ margin: "32px 0 16px 0" }}>
          Articles by {author.user?.username || author.username}
        </h2>
        <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
          <main style={{ flex: 2 }}>
            {articles.length > 0 ? (
              <ArticleList articles={articles} />
            ) : (
              <p>This author has not posted any articles yet.</p>
            )}
          </main>

          <aside style={{ flex: 1, minWidth: 280 }}>
            <section>
              <h3>Categories</h3>
              <CategoryList categories={categories} />
            </section>

            <section style={{ marginTop: 32 }}>
              <h3>Tags</h3>
              <TagList tags={tags} />
            </section>

          </aside>
        </div>
      </div>
    </>
  );
};

export default AuthorPage;
